import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { useForm, useFormState } from 'react-hook-form';
import NextLink from 'next/link';
import { AnimatePresence } from 'framer-motion';
import { always } from 'ramda';

import { CenterContainer } from '@components/centerContainer';
import {
  TextField,
  formMessages,
  CheckboxField,
  ButtonRightContainer,
  Button,
  BUTTON_SIZE,
  Link,
  Error,
} from '@components/form';
import { useYupValidationResolver } from '@hooks/useYup';
import { formatIntlLink } from '@utils/helpers';
import api from '@services/nextAPI';
import reportError from '@utils/reportError';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { HOME_ROUTE, ROUTE } from '@utils/constants';
import { MainAnimateContainer } from '@components/mainAnimateContainer';
import { LineTitle, TextMessage } from '@components/common';
import pintrk, { PINTEREST_EVENTS } from '@utils/pinterestTag';
import { useTrackConversions } from '@hooks/useTrackConversions';
import { CONVERSIONS_EVENTS, CONVERSIONS_STATUS } from '@definitions/conversions.types';

import { NEWSLETTER_EMAIL, NEWSLETTER_TERMS } from './newsletterView.constants';

interface FormProps {
  [NEWSLETTER_EMAIL]: string;
  [NEWSLETTER_TERMS]: boolean;
}

export const NewsletterView = () => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const { sendConversion } = useTrackConversions();
  const validationSchema = useMemo(
    () =>
      yup.object({
        [NEWSLETTER_EMAIL]: yup
          .string()
          .email(intl.formatMessage(formMessages.emailError))
          .required(intl.formatMessage(formMessages.required)),
        [NEWSLETTER_TERMS]: yup
          .bool()
          .oneOf([true], intl.formatMessage(formMessages.termsRequired))
          .required(intl.formatMessage(formMessages.termsRequired)),
      }),
    []
  );
  const resolver = useYupValidationResolver(validationSchema);
  const { register, handleSubmit, control } = useForm<FormProps>({
    defaultValues: {
      [NEWSLETTER_EMAIL]: '',
      [NEWSLETTER_TERMS]: false,
    },
    resolver,
  });
  const { errors } = useFormState({ control });

  const handleNewsletterSubmit = async ({ email }: FormProps) => {
    try {
      setLoading(true);
      setError(false);
      await api.post('email/subscribe', { email, tags: ['Footer Newsletter page'] });
      setSuccess(true);
      await sendConversion({
        eventType: CONVERSIONS_EVENTS.COMPLETE_REGISTRATION,
        status: CONVERSIONS_STATUS.NEWSLETTER,
      });
      pintrk.event(PINTEREST_EVENTS.SIGNUP, { lead_type: 'Newsletter' });
    } catch (e) {
      setError(true);
      reportError(e);
    } finally {
      setLoading(false);
    }
  };

  const renderError = renderWhenTrue(
    always(
      <Error>
        <FormattedMessage {...formMessages.generalError} />
      </Error>
    )
  );

  const renderForm = renderWhenTrueOtherwise(
    always(
      <MainAnimateContainer key="form">
        <form onSubmit={handleSubmit(handleNewsletterSubmit)}>
          <LineTitle>
            <FormattedMessage id="newsletterView.title" defaultMessage="Subscribe to our newsletter" />
          </LineTitle>
          <TextMessage>
            <FormattedMessage
              id="newsletterView.information"
              defaultMessage="Subscribe to our newsletter to be the first to find out about new monthly selections, events, and more."
            />
          </TextMessage>
          <TextField
            register={register(NEWSLETTER_EMAIL)}
            error={errors[NEWSLETTER_EMAIL]}
            placeholder={intl.formatMessage(formMessages.email)}
          />
          <CheckboxField
            register={register(NEWSLETTER_TERMS)}
            error={errors[NEWSLETTER_TERMS]}
            label={intl.formatMessage(formMessages.terms, {
              terms: formatIntlLink(ROUTE.TERMS_AND_CONDITIONS),
              privacy: formatIntlLink(ROUTE.PRIVACY_POLICY),
            })}
          />
          {renderError(error)}
          <ButtonRightContainer>
            <Button type="submit" disabled={loading} size={BUTTON_SIZE.L}>
              <FormattedMessage id="newsletterView.signUp" defaultMessage="Sign up" />
            </Button>
          </ButtonRightContainer>
        </form>
      </MainAnimateContainer>
    ),
    always(
      <MainAnimateContainer key="successMessage">
        <LineTitle>
          <FormattedMessage id="newsletterView.successTitle" defaultMessage="Subscription confirmed" />
        </LineTitle>
        <TextMessage>
          <FormattedMessage
            id="newsletterView.successInformation"
            defaultMessage="Thanks for joining us! You're on the list for first access to new artworks and exclusive content."
          />
        </TextMessage>
        <ButtonRightContainer>
          <a
            onClick={() => {
              window.history.pushState({}, '', HOME_ROUTE);
            }}
            href={HOME_ROUTE}
          >
            <Link size={BUTTON_SIZE.L}>
              <FormattedMessage id="newsletterView.returnHome" defaultMessage="DISCOVER NOW →" />
            </Link>
          </a>
        </ButtonRightContainer>
      </MainAnimateContainer>
    )
  );

  return (
    <CenterContainer>
      <AnimatePresence initial={false} exitBeforeEnter>
        {renderForm(!success)}
      </AnimatePresence>
    </CenterContainer>
  );
};
